import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";

const PROGRAM_LINKS = [
  { to: "/programs#adults", label: "Adults BJJ" },
  { to: "/programs#kids", label: "Kids (5–12)" },
  { to: "/programs#toddlers", label: "Toddlers (2.5–5)" },
  { to: "/programs#teens", label: "Teens" },
  { to: "/programs#self-defense", label: "Self-Defense" },
  { to: "/programs#verbal-jiu-jitsu", label: "Verbal Jiu-Jitsu™" },
  { to: "/after-school", label: "After-School Program" },
];

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/pricing", label: "Pricing" },
  { to: "/schedule", label: "Schedule" },
  { to: "/reviews", label: "Reviews" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [progOpen, setProgOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setProgOpen(false);
  }, [location.pathname, location.hash]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur shadow-sm py-3" : "bg-white/80 backdrop-blur py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group">
          <img src="/images/logo.png" alt="Alameda Brazilian Jiu-Jitsu Academy" className="h-11 w-11 rounded-full object-cover ring-1 ring-neutral-200" />
          <div className="leading-tight">
            <span className="block font-display font-extrabold text-[15px] tracking-tight text-[#1A1A1A]">ALAMEDA BJJ</span>
            <span className="block text-[10px] tracking-[0.22em] text-neutral-500">& SELF-DEFENSE</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
          <NavLink to="/" end className={({ isActive }) => `nav-link text-sm font-medium text-neutral-700 hover:text-[#1A1A1A] ${isActive ? "active text-[#1A1A1A]" : ""}`}>
            Home
          </NavLink>

          {/* Programs dropdown */}
          <div className="relative group">
            <button className="nav-link flex items-center gap-1 text-sm font-medium text-neutral-700 hover:text-[#1A1A1A] py-2">
              Programs <ChevronDown size={15} className="transition-transform group-hover:rotate-180" />
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
              <div className="w-64 bg-white rounded-xl shadow-xl border border-neutral-100 p-2">
                {PROGRAM_LINKS.map((p) => (
                  <Link key={p.to} to={p.to} className="block rounded-lg px-3.5 py-2.5 text-sm font-medium text-neutral-700 hover:bg-br-green/10 hover:text-br-green transition-colors">
                    {p.label}
                  </Link>
                ))}
                <Link to="/programs" className="block rounded-lg px-3.5 py-2.5 text-sm font-semibold text-br-green border-t border-neutral-100 mt-1">
                  View All Programs →
                </Link>
              </div>
            </div>
          </div>

          {LINKS.filter((l) => l.to !== "/").map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `nav-link text-sm font-medium text-neutral-700 hover:text-[#1A1A1A] whitespace-nowrap ${isActive ? "active text-[#1A1A1A]" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/trial">
            <Button className="bg-br-green hover:bg-[#00822f] text-white rounded-full px-6 shadow-sm shadow-br-green/30">Book Free Trial</Button>
          </Link>
        </div>

        <button className="lg:hidden text-[#1A1A1A]" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-neutral-100 mt-3 animate-fade-up max-h-[80vh] overflow-y-auto">
          <div className="px-5 py-4 flex flex-col gap-1">
            <NavLink to="/" end className={({ isActive }) => `py-2.5 text-[15px] font-medium border-b border-neutral-50 ${isActive ? "text-br-green" : "text-neutral-700"}`}>Home</NavLink>

            {/* Programs accordion */}
            <button onClick={() => setProgOpen((o) => !o)} className="flex items-center justify-between py-2.5 text-[15px] font-medium border-b border-neutral-50 text-neutral-700">
              Programs <ChevronDown size={17} className={`transition-transform ${progOpen ? "rotate-180" : ""}`} />
            </button>
            {progOpen && (
              <div className="pl-3 border-b border-neutral-50 pb-2">
                {PROGRAM_LINKS.map((p) => (
                  <Link key={p.to} to={p.to} className="block py-2 text-sm text-neutral-600 hover:text-br-green">{p.label}</Link>
                ))}
                <Link to="/programs" className="block py-2 text-sm font-semibold text-br-green">View All Programs →</Link>
              </div>
            )}

            {LINKS.filter((l) => l.to !== "/").map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `py-2.5 text-[15px] font-medium border-b border-neutral-50 ${isActive ? "text-br-green" : "text-neutral-700"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/newsletter" className="py-2.5 text-[15px] font-medium border-b border-neutral-50 text-neutral-700">Newsletter & Free eBook</Link>
            <Link to="/trial" className="mt-3">
              <Button className="w-full bg-br-green hover:bg-[#00822f] text-white rounded-full">Book Free Trial</Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
